import type { ReactNode } from 'react';
import Portal from '@/components/Portal';
import usePrefetchOnHover from '@/hooks/usePrefetchOnHover';
import { useModalState, useBodyScrollLock, useModalDataState } from '@/hooks/modals';
import type { DownloadConfig } from '@/types/modals';
import ModalHeader from './ModalHeader';
import ModalBody from './ModalBody';
import type { DateRange, ShowFilter } from '@/types';

type ModalProps = {
  title: string;
  children: (dateRange: DateRange, selectedShow: ShowFilter) => ReactNode;
  hashProp: string;
  showDateRangeFilter?: boolean;
  showShowsFilter?: boolean;
  renderTrigger?: (openModal: () => void, handlePrefetch: () => void) => ReactNode;
  onPrefetch?: () => void;
  onClose?: () => void;
  onOpen?: () => void;
  downloadConfig?: DownloadConfig;
  onDateRangeOpen?: () => void;
  onDateRangeApply?: (dateRange: DateRange) => void;
  onDateRangePresetSelect?: (preset: string) => void;
  onShowFilterOpen?: () => void;
  onShowFilterApply?: (selectedShow: ShowFilter) => void;
  actions?: (dateRange: DateRange, selectedShow: ShowFilter) => ReactNode;
};

/**
 * This component is used to render a modal.

 * @param title - The title of the modal.
 * @param children - The children of the modal.
 * @param hashProp - The hash prop of the modal.
 * @param showShowsFilter - Whether to show the shows filter.
 * @param showDateRangeFilter - Whether to show the date range filter.
 * @param renderTrigger - The trigger to render the modal.
 * @param onPrefetch - The function to prefetch the modal.
 * @param onClose - The function to close the modal.
 * @param onOpen - The function to call when the modal opens.
 * @param onDateRangeOpen - The function to when the date range filter is opened.
 * @param onDateRangePresetSelect - The function to when a date range preset is selected.
 * @param onDateRangeApply - The function to when the date range filter is applied.
 * @param downloadConfig - The download config for the modal.
 * @param onShowFilterOpen - The function to when the shows filter is opened.
 * @param onShowFilterApply - The function to when the shows filter is applied.
 * @param actions - Optional actions rendered next to the modal filters.
 * @returns The modal component.
 */
const Modal = ({
  title,
  children,
  hashProp,
  showShowsFilter,
  showDateRangeFilter,
  renderTrigger,
  onPrefetch,
  onClose,
  onOpen,
  onDateRangeOpen,
  onDateRangePresetSelect,
  onDateRangeApply,
  downloadConfig,
  onShowFilterOpen,
  onShowFilterApply,
  actions,
}: ModalProps) => {
  const { isModalOpen, openModal, closeModal } = useModalState(hashProp, onOpen);
  const { modalDateRange, handleDateRangeChange, modalSelectedShow, handleSelectedShowChange } =
    useModalDataState(isModalOpen);
  useBodyScrollLock(isModalOpen);

  const handlePrefetch = usePrefetchOnHover(onPrefetch ?? (() => {}), !isModalOpen);

  const handleDateRangeApply = (dateRange: DateRange) => {
    onDateRangeApply?.(dateRange);
    handleDateRangeChange(dateRange);
  };

  const handleSelectedShowApply = (selectedShow: ShowFilter) => {
    handleSelectedShowChange(selectedShow);
    onShowFilterApply?.(selectedShow);
  };

  const handleClose = () => {
    onClose?.();
    closeModal();
  };

  // If we have a renderTrigger and modal is closed, render only the trigger
  if (!isModalOpen) {
    if (renderTrigger) {
      return renderTrigger(openModal, handlePrefetch);
    }
    return null;
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-modal flex items-center justify-center">
        <div className="relative w-full h-full bg-white shadow-xl overflow-auto">
          <ModalHeader
            title={title}
            closeModal={handleClose}
            downloadConfig={downloadConfig}
            modalDateRange={modalDateRange}
            modalSelectedShow={modalSelectedShow}
          />

          <ModalBody
            modalSelectedShow={modalSelectedShow}
            onShowFilterOpen={onShowFilterOpen}
            onShowFilterApply={handleSelectedShowApply}
            showShowsFilter={showShowsFilter!}
            showDateRangeFilter={showDateRangeFilter!}
            modalDateRange={modalDateRange}
            handleDateRangeApply={handleDateRangeApply}
            onDateRangeOpen={onDateRangeOpen}
            onDateRangePresetSelect={onDateRangePresetSelect}
            children={children}
            actions={actions}
          />
        </div>
      </div>
    </Portal>
  );
};

export default Modal;
